import { useCallback, useState } from "react";

/**
 * The listing content panel's session memory (`WEB-005`,
 * `specs/product/listing-content-panel`, `AUT-231`, `product-journeys.md
 * § J1` step 7). One `sessionStorage` key for the whole product view --
 * never one per product, never one per tab -- so closing the drawer on
 * one product/tab keeps it closed on every other product and tab until
 * the browser session ends, and a new session opens it again.
 * `localStorage` is deliberately not used: that would survive the
 * session, which the journey does not ask for.
 */
export const LISTING_DRAWER_STORAGE_KEY = "listing-drawer:open";

function readIsOpen(): boolean {
  try {
    const raw = sessionStorage.getItem(LISTING_DRAWER_STORAGE_KEY);
    // No key yet -> open, per J1: "On the user's first product view of a
    // browsing session the panel is already open."
    if (raw === null) return true;
    return raw !== "false";
  } catch {
    return true;
  }
}

function writeIsOpen(open: boolean): void {
  try {
    sessionStorage.setItem(LISTING_DRAWER_STORAGE_KEY, open ? "true" : "false");
  } catch {
    // sessionStorage unavailable (e.g. private browsing) -- the toggle
    // still works for this render, it just will not persist.
  }
}

export interface UseListingDrawerResult {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export function useListingDrawer(): UseListingDrawerResult {
  const [isOpen, setIsOpen] = useState<boolean>(() => readIsOpen());

  const open = useCallback(() => {
    writeIsOpen(true);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    writeIsOpen(false);
    setIsOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      writeIsOpen(next);
      return next;
    });
  }, []);

  return { isOpen, open, close, toggle };
}
